// src/pages/ClaimDossier/components/TicketQrScanControl.tsx
// Quét mã QR in trên vé cho phân hệ Trả thưởng (màn Thẩm định hồ sơ + form Tạo hồ sơ KQ) — cùng cách đọc
// với Giao vé / Thu hồi vé: giải mã bằng decodeScannedTicket (xác thực chữ ký offline, fallback backend
// cho token AES cũ) ra {kỳ phát hành, ký hiệu, số vé}.
//
// 2 nguồn quét dùng song song:
//   - Nút "Quét QR" mở QrScannerModal (dùng chung với Tra cứu vé / Giao vé / Thu hồi vé) — trong modal người
//     dùng chọn nguồn: camera thiết bị, máy quét cổng COM (USB Serial) hoặc chụp/chọn ảnh vé.
//   - THIẾT BỊ QUÉT cầm tay kiểu bàn phím USB (hoặc dán link in trên vé): gõ vào ô nhập rồi Enter.
//     Ô nhập để UNCONTROLLED và đọc thẳng .value khi Enter — máy quét gõ rất nhanh, đọc từ React state có
//     thể bị cắt cụt token (xem chú thích tương tự ở QrScanSessionPage).

import React, { useRef, useState } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import QrScannerModal from 'pages/TicketLookup/components/QrScannerModal';
import { decodeTicketToken } from 'helpers/fakebackend_helper';
import { decodeScannedTicket, ScannedTicket } from 'helpers/ticketQrDecode';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier');

interface Props {
  onDecoded: (ticket: ScannedTicket) => void;
  disabled?: boolean;
  // Hiện ô nhập cho máy quét cầm tay / dán link (mặc định có).
  showInput?: boolean;
  buttonLabel?: string;
}

const TicketQrScanControl: React.FC<Props> = ({ onDecoded, disabled, showInput = true, buttonLabel }) => {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // QrScannerModal (camera/cổng COM) có thể trả nhiều lần đọc — chỉ xử lý lần ĐẦU TIÊN của mỗi lần mở modal.
  const scanHandledRef = useRef(false);

  const handleRawScan = async (raw: string) => {
    const value = (raw || '').trim();
    if (!value) return;
    setDecoding(true);
    try {
      const decoded = await decodeScannedTicket(value, decodeTicketToken);
      if (!decoded || !decoded.symbol || !decoded.ticketNumber) {
        toast.error(st('khong_doc_duoc_ma_qr_ve_message'));
        return;
      }
      onDecoded(decoded);
    } finally {
      setDecoding(false);
    }
  };

  const handleCameraScanned = (decodedText: string) => {
    if (scanHandledRef.current) return;
    scanHandledRef.current = true;
    setCameraOpen(false);
    handleRawScan(decodedText);
  };

  const handleOpenCamera = () => {
    scanHandledRef.current = false;
    setCameraOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    // Chặn submit form cha (form Tạo hồ sơ / form tra cứu) — Enter ở đây chỉ để giải mã QR.
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget;
    const value = target.value;
    target.value = '';
    handleRawScan(value);
  };

  return (
    <>
      <div className="d-flex gap-2 align-items-center">
        {showInput && (
          <Input
            type="text"
            innerRef={inputRef}
            className="form-control"
            placeholder={st('quet_may_hoac_dan_link_qr_placeholder')}
            onKeyDown={handleInputKeyDown}
            disabled={disabled || decoding}
            autoComplete="off"
          />
        )}
        <Button
          type="button"
          color="soft-primary"
          className="text-nowrap"
          onClick={handleOpenCamera}
          disabled={disabled || decoding}
          title={st('quet_qr_ve_bang_camera_title')}
        >
          {decoding ? <Spinner size="sm" className="me-1" /> : <i className="ri-qr-scan-2-line me-1"></i>}
          {buttonLabel || st('quet_qr_button')}
        </Button>
      </div>
      <QrScannerModal isOpen={cameraOpen} onClose={() => setCameraOpen(false)} onScanned={handleCameraScanned} />
    </>
  );
};

export default TicketQrScanControl;
